import { useMemo } from 'react';
import { startOfDay, isBefore } from 'date-fns';
import { useIntl } from 'react-intl';
import { Typography } from '@mui/material';
import type { Matcher } from 'react-day-picker';
import { useDateRangePickerContext } from '../context/DateRangePickerContext';
import { useWeekRangeState } from '../hooks/useWeekRangeState';
import { PresetSidebar } from '../components/PresetSidebar';
import { CalendarView } from '../components/CalendarView/CalendarView';
import { DateInputs } from '../components/DateInputs/DateInputs';
import { ActionButtons } from '../components/ActionButtons';
import { isValidDate } from '../utils/dateRange';
import './WeeklyPicker.css';

export function WeeklyPicker() {
  const {
    value,
    onChange,
    onClose,
    presetOptions,
    showPresets,
    hideDateInputs,
    hideButtons,
    disabledDates,
    onValidate,
    isComparison,
    mainRangeDuration,
  } = useDateRangePickerContext();

  const intl = useIntl();
  const today = useMemo(() => startOfDay(new Date()), []);

  const state = useWeekRangeState({
    value,
    onChange: v => {
      onChange(v);
      onClose();
    },
    onCancel: onClose,
    today,
    disabledDates,
    onValidate,
    isComparison,
    mainRangeDuration,
    intl,
    hideButtons,
  });

  const { draft, activeDateField, weekHoverModifiers, weeklyLimitAwareDisabledDates } = state;

  const modifiers = useMemo(() => {
    const base: Record<string, Matcher | Matcher[]> = {};

    if (weekHoverModifiers.weekHoverMiddle.length > 0) base.weekHoverMiddle = weekHoverModifiers.weekHoverMiddle;
    if (weekHoverModifiers.weekHoverEdge.length > 0) base.weekHoverEdge = weekHoverModifiers.weekHoverEdge;

    const from = isValidDate(draft.range.from) ? draft.range.from : undefined;
    const to = isValidDate(draft.range.to) ? draft.range.to : undefined;

    if (from && to) {
      base.range = isBefore(to, from) ? { from: to, to: from } : { from, to };
    } else if (from) {
      base.range = { from, to: from };
      base.pendingStart = from;
    }

    return base;
  }, [draft.range, weekHoverModifiers]);

  const selected = useMemo(() => {
    const from = isValidDate(draft.range.from) ? draft.range.from : undefined;
    const to = isValidDate(draft.range.to) ? draft.range.to : undefined;
    if (from && to && isBefore(to, from)) {
      return { from: to, to: from };
    }
    return { from, to };
  }, [draft.range]);

  const showApplyCancel = !hideButtons || draft.range.presetValue === 'custom';

  return (
    <>
      <div className="flex justify-between items-start border-b border-gray-200">
        {showPresets && (
          <div className="flex flex-col flex-shrink-0 w-[200px] min-h-[430px] border-r border-gray-300">
            <PresetSidebar
              onChange={state.handlePresetChange}
              value={draft.range.presetValue}
              options={presetOptions}
              className="pt-6 border-r-0"
            />
          </div>
        )}

        <div className="px-6 pt-6 flex flex-col gap-8">
          {!hideDateInputs && (
            <DateInputs
              onChange={state.handleCustomInputChange}
              from={draft.range.from}
              to={draft.range.to}
              today={today}
              onValidate={() => {}}
              validationData={state.valid}
              disabledDates={weeklyLimitAwareDisabledDates}
              activeField={activeDateField}
              rangeKey="range"
              onFocus={(f, r) => state.setActiveDateField({ field: f, range: r })}
              readOnlyTo={isComparison}
            />
          )}
          <CalendarView
            modifiers={modifiers}
            selected={selected}
            onDayClick={state.handleDayClick}
            onDayHover={state.handleDayHover}
            today={today}
            defaultMonth={state.calendarMonth}
            numberOfMonths={2}
            disabled={weeklyLimitAwareDisabledDates}
            selectionMode={activeDateField?.range ?? 'range'}
            disableDateSelection={false}
          />
          {hideDateInputs && (state.valid?.from || state.valid?.to) && (
            <div className="picker-validation-tooltip">
              {state.valid?.from && <Typography color="error">{state.valid.from}</Typography>}
              {state.valid?.to && (
                <Typography variant="body2" color="error">
                  {state.valid.to}
                </Typography>
              )}
            </div>
          )}
        </div>
      </div>

      <ActionButtons
        onApply={state.handleApply}
        onCancel={onClose}
        applyDisabled={!isValidDate(draft.range.from) || !isValidDate(draft.range.to)}
        invisible={!showApplyCancel}
      />
    </>
  );
}
